<script setup>
import { useTranslate } from '@/composables/useTranslate';
import logoMark from '@/assets/brand/logo-mark.png';

const { loading } = useTranslate();
</script>

<template>
  <transition name="gmloader">
    <div v-if="loading" class="gmloader" translate="no" role="status" aria-live="polite" aria-busy="true">
      <div class="gmloader__stage">
        <span class="gmloader__ring" />
        <span class="gmloader__ring gmloader__ring--2" />
        <img class="gmloader__logo" :src="logoMark" alt="Good Morning Tours & Safari" />
      </div>
      <span class="gmloader__tag">&ldquo;The best is yet to come.&rdquo;</span>
    </div>
  </transition>
</template>

<style scoped>
.gmloader {
  position: fixed; inset: 0; z-index: 99999;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 26px;
  background: radial-gradient(120% 110% at 50% 38%, #3C1A11 0%, #200E09 55%, #150906 100%);
}

.gmloader__stage { position: relative; display: grid; place-items: center; width: 320px; height: 320px; }

.gmloader__logo {
  position: relative; z-index: 2; width: 220px; max-width: 60vw; height: auto; display: block;
  filter: drop-shadow(0 14px 30px rgba(0,0,0,.55));
  /* Promote to its own GPU layer and animate transform only, so the pulse stays
     smooth even while the page is busy reloading / Google is translating. */
  will-change: transform; transform: translateZ(0); backface-visibility: hidden;
  animation: gmloader-pulse 1.7s ease-in-out infinite;
}

.gmloader__ring {
  position: absolute; inset: 0; border-radius: 50%;
  background: radial-gradient(circle, rgba(232,193,94,.32) 0%, rgba(232,193,94,0) 62%);
  animation: gmloader-glow 1.7s ease-in-out infinite;
}
.gmloader__ring--2 { animation-delay: .85s; }

.gmloader__tag {
  font-family: var(--font-display, Georgia, serif); font-style: italic; font-size: 16px;
  color: var(--sand-300); letter-spacing: .01em; text-shadow: 0 2px 14px rgba(0,0,0,.6);
  animation: gmloader-fade 1.7s ease-in-out infinite;
}

@keyframes gmloader-pulse {
  0%, 100% { transform: translateZ(0) scale(1); }
  50% { transform: translateZ(0) scale(1.08); }
}
@keyframes gmloader-glow {
  0%, 100% { opacity: .45; transform: scale(.85); }
  50% { opacity: 1; transform: scale(1.2); }
}
@keyframes gmloader-fade {
  0%, 100% { opacity: .6; }
  50% { opacity: 1; }
}

/* Fade the whole overlay out once loading finishes. */
.gmloader-enter-active, .gmloader-leave-active { transition: opacity .5s ease; }
.gmloader-enter-from, .gmloader-leave-to { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .gmloader__logo, .gmloader__ring, .gmloader__tag { animation: none; }
}
</style>
